/**
 * App-side remediation catalog for the Security tab (`ServerSecurityTab.vue`).
 *
 * Keyed by the exact `security.audit` check ids the luna-node agent emits.
 * Purely static/frontend — no agent release or backend route needed. Each
 * entry tells the operator precisely how to fix a non-passing check:
 *
 *   summary       — one line: what to change and why it matters.
 *   evidence_hint — how to read the check's `evidence` block.
 *   warning       — safety caveats (⚠), especially lock-out risks. May carry
 *                   several lines separated by "\n" (rendered `white-space:
 *                   pre-line`).
 *   variants      — ordered list of `{ key, label, steps }`. `key` is
 *                   'common' (OS-agnostic, no label shown), 'debian'
 *                   (Debian/Ubuntu family) or 'rhel' (RHEL/EL family). Each
 *                   step is `{ text, code? }` — the instruction plus an
 *                   optional exact directive/command rendered monospace.
 *
 * The server's OS family is NOT persisted on the Server entity today (the
 * agent hello carries `os`/`kernel` but `TelemetryService` does not store
 * them), so {@link visibleVariantsFor} falls back to showing BOTH distro
 * variants clearly labeled. If an `os` string ever becomes available it flows
 * through {@link detectOsFamily} and only the matching variant is shown.
 */

const CATALOG = {
  'sshd.password_auth': {
    summary:
      'Disable SSH password authentication so only key-based logins are accepted — password auth exposes the host to brute-force and credential-stuffing attacks.',
    evidence_hint:
      "The evidence shows the effective setting the agent read (via `sshd -T`); a value of `yes` means password login is currently allowed.",
    warning:
      '⚠ Confirm key-based login works in a SEPARATE, still-open SSH session BEFORE reloading — disabling password auth without a working key locks you out.\n'
      + '⚠ On Ubuntu cloud images cloud-init often drops `PasswordAuthentication yes` into /etc/ssh/sshd_config.d/60-cloudimg-settings.conf, which overrides the main file — check the .d/ directory too.',
    variants: [
      {
        key: 'common',
        label: '',
        steps: [
          { text: 'Edit the SSH server config (and check /etc/ssh/sshd_config.d/*.conf for overrides that re-enable it):', code: '/etc/ssh/sshd_config' },
          { text: 'Set the directive:', code: 'PasswordAuthentication no' },
          { text: 'Apply the change:', code: 'sudo systemctl reload sshd' },
          { text: 'Verify (expect "no"):', code: 'sudo sshd -T | grep -i passwordauthentication' },
        ],
      },
    ],
  },

  'sshd.permit_root_login': {
    summary:
      'Restrict root SSH login. Use `prohibit-password` to allow key-based root but block password root, or `no` to block root SSH entirely (preferred when a non-root sudo user exists).',
    evidence_hint: 'The evidence shows the current effective PermitRootLogin value.',
    warning:
      '⚠ Ensure a non-root sudo user can log in first — `no` removes root SSH access entirely and locks out root if no other working account exists.',
    variants: [
      {
        key: 'common',
        label: '',
        steps: [
          { text: 'Edit the SSH server config:', code: '/etc/ssh/sshd_config' },
          { text: 'Set the directive (or `no` to block root SSH entirely):', code: 'PermitRootLogin prohibit-password' },
          { text: 'Apply the change:', code: 'sudo systemctl reload sshd' },
          { text: 'Verify:', code: 'sudo sshd -T | grep -i permitrootlogin' },
        ],
      },
    ],
  },

  'sshd.port': {
    summary:
      'Informational — SSH is not on the default port 22, or you may choose to move it. Moving off 22 cuts automated scan noise but is not a substitute for key-based auth.',
    evidence_hint: 'The evidence shows the port(s) sshd is currently listening on.',
    warning:
      '⚠ Open the new port in the firewall BEFORE reloading sshd — reloading first can drop your ability to reconnect and lock you out.',
    variants: [
      {
        key: 'debian',
        label: 'Debian / Ubuntu',
        steps: [
          { text: 'Set the port in the SSH server config:', code: 'Port <n>   # in /etc/ssh/sshd_config' },
          { text: 'Open the new port in the firewall:', code: 'sudo ufw allow <n>/tcp' },
          { text: 'Reload sshd:', code: 'sudo systemctl reload sshd' },
          { text: 'Verify:', code: 'sudo sshd -T | grep -i port' },
        ],
      },
      {
        key: 'rhel',
        label: 'RHEL / EL',
        steps: [
          { text: 'Set the port in the SSH server config:', code: 'Port <n>   # in /etc/ssh/sshd_config' },
          { text: 'Open the new port in the firewall:', code: 'sudo firewall-cmd --add-port=<n>/tcp --permanent && sudo firewall-cmd --reload' },
          { text: 'On SELinux systems, label the port for SSH:', code: 'sudo semanage port -a -t ssh_port_t -p tcp <n>' },
          { text: 'Reload sshd:', code: 'sudo systemctl reload sshd' },
          { text: 'Verify:', code: 'sudo sshd -T | grep -i port' },
        ],
      },
    ],
  },

  'fs.world_writable_web': {
    summary:
      'World-writable files or directories under the web root let any local user modify served content or code — remove the world-write bit after reviewing each path.',
    evidence_hint: 'The paths listed in the evidence below are the offending world-writable files/directories — review each before changing it.',
    warning:
      "⚠ Review first — don't blind-chmod. A legitimately writable cache/upload directory usually needs its OWNERSHIP fixed (chown to the web user) rather than its world-write bit removed.",
    variants: [
      {
        key: 'common',
        label: '',
        steps: [
          { text: 'Review each path listed in the evidence above.' },
          { text: 'Remove world-write from a single file:', code: 'sudo chmod o-w <path>' },
          { text: 'Bulk-fix files under the web root:', code: 'sudo find /var/www -type f -perm -o+w -exec chmod o-w {} +' },
          { text: 'Bulk-fix directories (this preserves the sticky/exec bits):', code: 'sudo find /var/www -type d -perm -o+w -exec chmod o-w {} +' },
          { text: 'Verify (should list nothing):', code: 'sudo find /var/www -perm -o+w' },
        ],
      },
    ],
  },

  'fs.sensitive_perms': {
    summary:
      'Sensitive files (.env, SSH private keys) are readable or writable too broadly — tighten them to owner-only so secrets are not exposed to other local users.',
    evidence_hint: 'The evidence names the over-permissive files (e.g. .env, private keys) and their current mode.',
    warning:
      '⚠ Make sure the file OWNER is the process that needs it. For a root-owned .env the web user must read, prefer `chmod 640` plus `chown root:www-data` over loosening it to world-readable.',
    variants: [
      {
        key: 'common',
        label: '',
        steps: [
          { text: 'Restrict .env files to owner-only read/write:', code: 'sudo chmod 600 <path>' },
          { text: 'Restrict SSH private keys:', code: 'chmod 600 <keyfile>' },
          { text: 'Restrict the ~/.ssh directory:', code: 'chmod 700 ~/.ssh' },
          { text: 'Verify:', code: 'ls -l <path>' },
        ],
      },
    ],
  },

  'sudo.nopasswd': {
    summary:
      'Passwordless sudo (NOPASSWD) lets a compromised account escalate to root with no further check — review each entry and tighten any broader than it needs to be.',
    evidence_hint: 'The evidence lists the NOPASSWD sudoers entries the agent found.',
    warning:
      '⚠ Some NOPASSWD entries are legitimate (CI/deploy automation, this platform\'s own agent). This is a REVIEW prompt, not an auto-remove — document which are intentional before touching them.\n'
      + '⚠ Only ever edit sudoers via `sudo visudo` — a syntax error saved directly can lock out sudo entirely.',
    variants: [
      {
        key: 'common',
        label: '',
        steps: [
          { text: 'Review the main sudoers file and every drop-in:', code: '/etc/sudoers  and  /etc/sudoers.d/*' },
          { text: 'Edit safely — visudo validates syntax on save:', code: 'sudo visudo -f /etc/sudoers.d/<file>' },
          { text: 'Remove or tighten `NOPASSWD:` where the passwordless scope is broader than needed (restrict to specific commands rather than ALL).' },
          { text: 'Verify a user\'s effective sudo rights:', code: 'sudo -l -U <user>' },
        ],
      },
    ],
  },

  'os.security_updates': {
    summary:
      'Pending security updates leave known-exploitable vulnerabilities unpatched — apply them, and consider enabling automatic security updates.',
    evidence_hint: 'The evidence lists the count (and often the names) of the pending security updates.',
    warning:
      '⚠ Schedule a maintenance window — some updates require a service restart or a reboot. On EL, `needs-restarting -r` tells you whether a reboot is required.',
    variants: [
      {
        key: 'debian',
        label: 'Debian / Ubuntu',
        steps: [
          { text: 'Apply security updates:', code: 'sudo apt-get update && sudo apt-get upgrade' },
          { text: 'Automate future updates (optional):', code: 'sudo dpkg-reconfigure -plow unattended-upgrades' },
          { text: 'Verify by re-running the audit, or preview what remains:', code: 'apt-get -s upgrade' },
        ],
      },
      {
        key: 'rhel',
        label: 'RHEL / EL',
        steps: [
          { text: 'Apply security updates:', code: 'sudo dnf upgrade --security' },
          { text: 'Automate future updates (optional):', code: 'sudo dnf install dnf-automatic && sudo systemctl enable --now dnf-automatic.timer' },
          { text: 'Check whether a reboot is required:', code: 'needs-restarting -r' },
          { text: 'Verify by re-running the audit.' },
        ],
      },
    ],
  },

  'web.cf_client_ip_visibility': {
    summary:
      'This Cloudflare-fronted vhost logs Cloudflare\'s own edge IPs, not the real visitor — every request collapses onto a handful of CF addresses, which makes per-IP intrusion detection (Plan 008\'s `http.flood` rule) useless here and misattributes any "attacker" to Cloudflare itself. Restoring the real client IP fixes both the logs and the detection.',
    evidence_hint:
      'The evidence shows the sampled client IPs the agent observed in this vhost\'s access log — when they fall inside Cloudflare\'s published ranges instead of showing real visitor addresses, the vhost is edge-only.',
    warning:
      '⚠ The "Restore real client IPs" button above re-runs this as `web.cf_realip.apply` — it config-tests before reloading and refuses to reload on a failing test, but confirm the vhost is genuinely behind Cloudflare (not just any reverse proxy) before applying: `RemoteIPTrustedProxy`/`set_real_ip_from` only trusts Cloudflare\'s published ranges, so if traffic reaches this box directly (not via CF) the restored header can never be spoofed by an attacker — but it also then does nothing useful.',
    variants: [
      {
        key: 'debian',
        label: 'Debian / Ubuntu (Apache)',
        steps: [
          { text: 'Enable the remote-IP module:', code: 'sudo a2enmod remoteip' },
          { text: 'Add a config trusting Cloudflare\'s published ranges:', code: '/etc/apache2/conf-available/cf-remoteip.conf' },
          { text: 'Inside it, set the header and one RemoteIPTrustedProxy per CF range:', code: 'RemoteIPHeader CF-Connecting-IP\nRemoteIPTrustedProxy <cf-range> ...' },
          { text: 'Enable the config and ensure the vhost\'s LogFormat uses %a (not %h):', code: 'sudo a2enconf cf-remoteip' },
          { text: 'Config-test, then reload:', code: 'sudo apachectl configtest && sudo systemctl reload apache2' },
          { text: 'Verify by re-running the audit — sampled client IPs should now be real visitors, not CF edge addresses.' },
        ],
      },
      {
        key: 'rhel',
        label: 'RHEL / EL (Apache)',
        steps: [
          { text: 'Enable the remote-IP module (usually built-in as mod_remoteip.so):', code: 'sudo dnf install httpd' },
          { text: 'Add a config trusting Cloudflare\'s published ranges:', code: '/etc/httpd/conf.d/cf-remoteip.conf' },
          { text: 'Inside it, set the header and one RemoteIPTrustedProxy per CF range:', code: 'RemoteIPHeader CF-Connecting-IP\nRemoteIPTrustedProxy <cf-range> ...' },
          { text: 'Ensure the vhost\'s LogFormat uses %a (not %h).' },
          { text: 'Config-test, then reload:', code: 'sudo apachectl configtest && sudo systemctl reload httpd' },
          { text: 'Verify by re-running the audit — sampled client IPs should now be real visitors, not CF edge addresses.' },
        ],
      },
      {
        key: 'common',
        label: 'nginx (any distro)',
        steps: [
          { text: 'Ensure ngx_http_realip_module is available (built into most distro packages).' },
          { text: 'Add one set_real_ip_from per Cloudflare published range, plus the header directive:', code: 'set_real_ip_from <cf-range>;\nreal_ip_header CF-Connecting-IP;' },
          { text: 'Config-test, then reload:', code: 'sudo nginx -t && sudo systemctl reload nginx' },
          { text: 'Verify by re-running the audit — sampled client IPs should now be real visitors, not CF edge addresses.' },
        ],
      },
    ],
  },
};

/**
 * Older agent builds emitted `apt.security_updates`; current builds emit the
 * distro-neutral `os.security_updates`. Both map to the same entry.
 */
const ID_ALIASES = {
  'apt.security_updates': 'os.security_updates',
};

/** Return the remediation entry for a check id (handling legacy aliases), or null. */
export function getRemediation(checkId) {
  if (!checkId) {
    return null;
  }
  const key = ID_ALIASES[checkId] || checkId;
  return CATALOG[key] || null;
}

/**
 * Map an OS/kernel string (e.g. "ubuntu-22.04", "Rocky Linux 9") to a distro
 * family key, or null when it can't be determined.
 */
export function detectOsFamily(os) {
  if (!os) {
    return null;
  }
  const s = String(os).toLowerCase();
  if (/(debian|ubuntu|mint|raspbian|devuan)/.test(s)) {
    return 'debian';
  }
  if (/(rhel|red\s?hat|centos|rocky|alma|fedora|amazon|amzn|oracle|\bol\d)/.test(s)) {
    return 'rhel';
  }
  return null;
}

/**
 * Decide which variants of an entry to render for a given OS family:
 *  - a single-variant (OS-agnostic) entry always shows that one;
 *  - a debian/rhel entry shows only the matching family when known;
 *  - when the family is unknown, BOTH are shown, each clearly labeled.
 */
export function visibleVariantsFor(remediation, osFamily) {
  const variants = (remediation && remediation.variants) || [];
  if (variants.length <= 1) {
    return variants;
  }
  if (osFamily) {
    const match = variants.filter((v) => v.key === osFamily || v.key === 'common');
    if (match.length) {
      return match;
    }
  }
  return variants;
}

export default CATALOG;
